import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  Shield, 
  Check, 
  ChevronRight, 
  ChevronLeft,
  Rocket,
  FileCheck,
  Plug,
  Upload,
  PartyPopper,
  User,
  Building,
  Loader2
} from 'lucide-react';
import api from '../lib/api';

const STEPS = [
  { id: 'welcome', title: 'Welcome', icon: Rocket },
  { id: 'frameworks', title: 'Select Frameworks', icon: Shield },
  { id: 'integrations', title: 'Connect Tools', icon: Plug },
  { id: 'evidence', title: 'First Evidence', icon: Upload },
  { id: 'complete', title: 'All Set!', icon: PartyPopper }
];

const AVAILABLE_FRAMEWORKS = [
  { id: 'soc2', name: 'SOC 2 Type II', controls: 49, description: 'Service Organization Control - Trust Services Criteria', popular: true },
  { id: 'iso27001', name: 'ISO 27001', controls: 54, description: 'International Information Security Standard', popular: true },
  { id: 'gdpr', name: 'GDPR', controls: 36, description: 'EU General Data Protection Regulation', popular: false },
  { id: 'hipaa', name: 'HIPAA', controls: 30, description: 'US Healthcare Privacy & Security', popular: false },
  { id: 'pci-dss', name: 'PCI-DSS', controls: 63, description: 'Payment Card Industry Data Security', popular: true },
  { id: 'nist', name: 'NIST 800-53', controls: 142, description: 'US Federal Security Controls', popular: false },
  { id: 'fedramp', name: 'FedRAMP', controls: 84, description: 'US Government Cloud Security', popular: false }
];

const INTEGRATIONS = [
  { id: 'aws', name: 'AWS', icon: '☁️', description: 'S3, IAM, CloudTrail monitoring', category: 'Cloud' },
  { id: 'github', name: 'GitHub', icon: '🐙', description: 'Branch protection, vulnerability alerts', category: 'DevOps' },
  { id: 'okta', name: 'Okta', icon: '🔐', description: 'SSO, MFA policies, user management', category: 'Identity' },
  { id: 'slack', name: 'Slack', icon: '💬', description: 'Compliance alerts and notifications', category: 'Communication' },
  { id: 'teams', name: 'Microsoft Teams', icon: '👥', description: 'Compliance alerts and notifications', category: 'Communication' }
];

export default function OnboardingPage() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [onboardingData, setOnboardingData] = useState({
    companyName: '',
    industry: '',
    teamSize: '',
    selectedFrameworks: [],
    connectedIntegrations: [],
    firstEvidenceUploaded: false
  });
  const [seedingFrameworks, setSeedingFrameworks] = useState(false);

  const progress = ((currentStep + 1) / STEPS.length) * 100;

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleFrameworkToggle = (frameworkId) => {
    setOnboardingData(prev => ({
      ...prev,
      selectedFrameworks: prev.selectedFrameworks.includes(frameworkId)
        ? prev.selectedFrameworks.filter(id => id !== frameworkId)
        : [...prev.selectedFrameworks, frameworkId]
    }));
  };

  const handleIntegrationConnect = (integrationId) => {
    // In a real app, this would open an OAuth flow or configuration modal
    toast.info(`${integrationId} integration requires API keys. Configure in Settings > Integrations after onboarding.`);
    setOnboardingData(prev => ({
      ...prev,
      connectedIntegrations: prev.connectedIntegrations.includes(integrationId)
        ? prev.connectedIntegrations
        : [...prev.connectedIntegrations, integrationId]
    }));
  };

  const seedSelectedFrameworks = async () => {
    setSeedingFrameworks(true);
    const frameworkEndpoints = {
      'soc2': '/api/seed/soc2',
      'iso27001': '/api/seed/iso27001',
      'gdpr': '/api/seed/gdpr',
      'hipaa': '/api/seed/hipaa',
      'pci-dss': '/api/seed/pci-dss',
      'nist': '/api/seed/nist-800-53',
      'fedramp': '/api/seed/fedramp'
    };

    for (const frameworkId of onboardingData.selectedFrameworks) {
      const endpoint = frameworkEndpoints[frameworkId];
      if (endpoint) {
        try {
          await api.post(endpoint);
        } catch (error) {
          // Framework might already exist, which is fine
          console.log(`Framework ${frameworkId} may already exist`);
        }
      }
    }
    setSeedingFrameworks(false);
    toast.success(`${onboardingData.selectedFrameworks.length} framework(s) configured!`);
  };

  const handleCompleteOnboarding = async () => {
    setLoading(true);
    try {
      // Mark onboarding as complete
      await api.post('/api/onboarding/complete', {
        selectedFrameworks: onboardingData.selectedFrameworks,
        connectedIntegrations: onboardingData.connectedIntegrations
      });
      
      toast.success('Onboarding complete! Welcome to Zentry.');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Failed to complete onboarding');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (STEPS[currentStep].id) {
      case 'welcome':
        return (
          <div className="text-center py-8">
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Rocket className="w-10 h-10 text-blue-600" />
            </div>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Welcome to Zentry!</h2>
            <p className="text-lg text-slate-600 mb-8 max-w-md mx-auto">
              Let&apos;s get your compliance program set up in just a few minutes. 
              We&apos;ll help you select frameworks, connect your tools, and upload your first evidence.
            </p>
            
            <div className="max-w-sm mx-auto space-y-4 text-left">
              <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg">
                <User className="w-5 h-5 text-slate-600 mt-0.5" />
                <div>
                  <p className="font-medium text-slate-900">Quick Setup</p>
                  <p className="text-sm text-slate-600">Takes about 3-5 minutes</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg">
                <Building className="w-5 h-5 text-slate-600 mt-0.5" />
                <div>
                  <p className="font-medium text-slate-900">Personalized</p>
                  <p className="text-sm text-slate-600">We&apos;ll tailor the platform to your needs</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg">
                <Shield className="w-5 h-5 text-slate-600 mt-0.5" />
                <div>
                  <p className="font-medium text-slate-900">Skip Anytime</p>
                  <p className="text-sm text-slate-600">You can always configure later</p>
                </div>
              </div>
            </div>

            <div className="mt-6 p-4 bg-emerald-50 border border-emerald-200 rounded-lg max-w-sm mx-auto text-left">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-emerald-600 mt-0.5" />
                <div>
                  <p className="font-medium text-emerald-900">CTEM Module Included</p>
                  <p className="text-sm text-emerald-700">
                    Continuous Threat Exposure Management — proactively discover, prioritize, and remediate threats across your attack surface.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'frameworks':
        return (
          <div className="py-4">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Select Your Compliance Frameworks</h2>
              <p className="text-slate-600">Choose the frameworks you need to comply with. You can add more later.</p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-4 max-w-3xl mx-auto">
              {AVAILABLE_FRAMEWORKS.map((framework) => (
                <div
                  key={framework.id}
                  onClick={() => handleFrameworkToggle(framework.id)}
                  className={`relative p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    onboardingData.selectedFrameworks.includes(framework.id)
                      ? 'border-blue-600 bg-blue-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  {framework.popular && (
                    <Badge className="absolute -top-2 -right-2 bg-amber-500">Popular</Badge>
                  )}
                  <div className="flex items-start gap-3">
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                      onboardingData.selectedFrameworks.includes(framework.id)
                        ? 'border-blue-600 bg-blue-600'
                        : 'border-slate-300'
                    }`}>
                      {onboardingData.selectedFrameworks.includes(framework.id) && (
                        <Check className="w-4 h-4 text-white" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-slate-900">{framework.name}</h3>
                        <span className="text-xs text-slate-500">({framework.controls} controls)</span>
                      </div>
                      <p className="text-sm text-slate-600 mt-1">{framework.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {onboardingData.selectedFrameworks.length > 0 && (
              <div className="mt-6 text-center">
                <Button 
                  onClick={seedSelectedFrameworks} 
                  disabled={seedingFrameworks}
                  variant="outline"
                  className="mr-4"
                >
                  {seedingFrameworks ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Setting up...
                    </>
                  ) : (
                    <>
                      <FileCheck className="w-4 h-4 mr-2" />
                      Configure {onboardingData.selectedFrameworks.length} Framework(s)
                    </>
                  )}
                </Button>
              </div>
            )}

            <p className="text-center text-sm text-slate-500 mt-6">
              Selected: {onboardingData.selectedFrameworks.length} framework(s)
            </p>
          </div>
        );

      case 'integrations':
        return (
          <div className="py-4">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Connect Your Tools</h2>
              <p className="text-slate-600">Automate evidence collection by connecting your cloud and DevOps tools.</p>
            </div>
            
            <div className="max-w-2xl mx-auto space-y-4">
              {['Cloud', 'DevOps', 'Identity', 'Communication'].map((category) => (
                <div key={category}>
                  <h3 className="text-sm font-medium text-slate-500 mb-2">{category}</h3>
                  <div className="grid md:grid-cols-2 gap-3">
                    {INTEGRATIONS.filter(i => i.category === category).map((integration) => (
                      <div
                        key={integration.id}
                        className="flex items-center justify-between p-4 bg-white border border-slate-200 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">{integration.icon}</span>
                          <div>
                            <h4 className="font-medium text-slate-900">{integration.name}</h4>
                            <p className="text-xs text-slate-500">{integration.description}</p>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant={onboardingData.connectedIntegrations.includes(integration.id) ? "default" : "outline"}
                          onClick={() => handleIntegrationConnect(integration.id)}
                        >
                          {onboardingData.connectedIntegrations.includes(integration.id) ? (
                            <>
                              <Check className="w-4 h-4 mr-1" />
                              Noted
                            </>
                          ) : (
                            'Connect'
                          )}
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 p-4 bg-blue-50 rounded-lg max-w-2xl mx-auto">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> Integration setup requires API keys which you can configure after onboarding in Settings → Integrations. 
                For now, we&apos;re noting your preferences.
              </p>
            </div>
          </div>
        );

      case 'evidence':
        return (
          <div className="py-4">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Upload Your First Evidence</h2>
              <p className="text-slate-600">Get started by uploading a policy document or security screenshot.</p>
            </div>
            
            <div className="max-w-md mx-auto">
              <div 
                className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors cursor-pointer"
                onClick={() => {
                  toast.info('You can upload evidence after onboarding in the Evidence Lab.');
                  setOnboardingData(prev => ({ ...prev, firstEvidenceUploaded: true }));
                }}
              >
                <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                <p className="font-medium text-slate-700 mb-1">Click to upload</p>
                <p className="text-sm text-slate-500">PDF, PNG, JPG up to 10MB</p>
              </div>

              <div className="mt-6 space-y-3">
                <p className="text-sm font-medium text-slate-700">Suggested first uploads:</p>
                <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                  <FileCheck className="w-5 h-5 text-slate-500" />
                  <span className="text-sm text-slate-600">Information Security Policy</span>
                </div>
                <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                  <FileCheck className="w-5 h-5 text-slate-500" />
                  <span className="text-sm text-slate-600">Access Control Policy</span>
                </div>
                <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                  <FileCheck className="w-5 h-5 text-slate-500" />
                  <span className="text-sm text-slate-600">Network Diagram</span>
                </div>
              </div>

              <Button 
                variant="ghost" 
                className="w-full mt-6"
                onClick={() => {
                  setOnboardingData(prev => ({ ...prev, firstEvidenceUploaded: true }));
                  handleNext();
                }}
              >
                Skip for now
              </Button>
            </div>
          </div>
        );

      case 'complete':
        return (
          <div className="text-center py-8">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <PartyPopper className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">You&apos;re All Set!</h2>
            <p className="text-lg text-slate-600 mb-8 max-w-md mx-auto">
              Your Zentry workspace is ready. Here&apos;s a summary of your setup:
            </p>
            
            <div className="max-w-sm mx-auto space-y-4 text-left mb-8">
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <span className="text-slate-600">Frameworks Selected</span>
                <span className="font-semibold text-slate-900">{onboardingData.selectedFrameworks.length}</span>
              </div>
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <span className="text-slate-600">Integrations Noted</span>
                <span className="font-semibold text-slate-900">{onboardingData.connectedIntegrations.length}</span>
              </div>
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <span className="text-slate-600">Trial Period</span>
                <span className="font-semibold text-green-600">14 days remaining</span>
              </div>
              <div className="flex items-center justify-between p-4 bg-emerald-50 rounded-lg">
                <span className="text-emerald-700">CTEM Module</span>
                <span className="font-semibold text-emerald-600">Active</span>
              </div>
            </div>

            <div className="space-y-3 max-w-sm mx-auto">
              <Button 
                size="lg" 
                className="w-full bg-slate-900 hover:bg-slate-800"
                onClick={handleCompleteOnboarding}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Finishing...
                  </>
                ) : (
                  <>
                    Go to Dashboard
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-slate-900 rounded flex items-center justify-center">
                <Shield className="w-4 h-4 text-white" />
              </div>
              <span className="font-bold text-slate-900">Zentry</span>
            </div>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate('/dashboard')}
            >
              Skip Setup
            </Button>
          </div>
        </div>
      </div>

      {/* Progress */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-2">
            {STEPS.map((step, index) => (
              <div 
                key={step.id}
                className={`flex items-center gap-2 ${
                  index <= currentStep ? 'text-blue-600' : 'text-slate-400'
                }`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  index < currentStep 
                    ? 'bg-blue-600 text-white' 
                    : index === currentStep 
                      ? 'bg-blue-100 text-blue-600 border-2 border-blue-600' 
                      : 'bg-slate-100 text-slate-400'
                }`}>
                  {index < currentStep ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <step.icon className="w-4 h-4" />
                  )}
                </div>
                <span className="hidden md:inline text-sm font-medium">{step.title}</span>
              </div>
            ))}
          </div>
          <Progress value={progress} className="h-1" />
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        <Card className="border-0 shadow-lg">
          <CardContent className="p-8">
            {renderStepContent()}
          </CardContent>
        </Card>

        {/* Navigation */}
        {currentStep < STEPS.length - 1 && (
          <div className="flex items-center justify-between mt-6">
            <Button
              variant="ghost"
              onClick={handleBack}
              disabled={currentStep === 0}
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <Button
              onClick={handleNext}
              disabled={currentStep === 1 && onboardingData.selectedFrameworks.length === 0}
            >
              {currentStep === STEPS.length - 2 ? 'Finish' : 'Continue'}
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
